<template>
  <div
    class="demo-tile"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Mobile layout: 4-row flexbox -->
    <div class="demo-tile-mob fd-c p-hor bs-bb">
      <div class="d-f fd-c p-ver b-bottom">
        <!-- Demo mode -->
        <template v-if="!showMessage">
          <!-- Row 1: avatar + title/subtitle (artist or "Sent to: label") + 2m ago NEW + more -->
          <div class="mob-row mob-row-1 d-f ai-c jc-sb g-sm bs-bb">
            <div
              class="mob-title-block d-f ai-c flex-1 min-w-0 custom-btn"
              @click="titleClicked"
            >
              <ImageWithInitials :size="40" :src="profileImage" :name="imageText" class="mob-avatar flex-shrink-0" />
              <div class="mob-title-subtitle d-f fd-c flex-1 min-w-0 of-h">
                <div class="d-f ai-c">
                  <div class="d-f ai-c flex-1 of-h bs-bb">
                    <span class="mob-title truncate">{{ demo.title }}</span>
                    <IconBase :w="10" :h="10" viewBox="0 0 15 15" sw="1.5" class="m-l-sm m-r-sm fs-0">
                      <IconChevronRight />
                    </IconBase>
                  </div>
                  <div
                    class="mob-more-wrap p-rel fs-0 d-f ai-c jc-c"
                    :class="{ 'mob-more-wrap-open': moreOpen }"
                    @click.stop="moreOpen = !moreOpen"
                  >
                    <div
                      type="button"
                      class="mob-more-btn p-l d-f"
                      aria-haspopup="true"
                      :aria-expanded="moreOpen"
                    >
                      <IconBase :w="15" :h="15" view-box="0 0 15 15" filled>
                        <IconMore />
                      </IconBase>
                    </div>
                  </div>
                </div>
                <div class="d-f ai-c">
                  <span class="mob-subtitle mute truncate">
                    {{ !isReceiver ? "Sent to: " + submittedToName : submittedByName }}
                  </span>
                  <div class="d-f ai-c m-l-sm">
                    <span class="mute"><TimestampCountdown :start="timeSince" /></span>
                    <span v-if="notViewedFlag" class="mob-new m-l-sm">NEW</span>
                    <!-- <span v-if="demo.timestamp" class="m-l-sm font-small" :class="daysUntilOriginalExpiry <= 7 ? 'expiry-warning' : 'mute'">{{ originalExpiryShort }}</span> -->
                  </div>
                </div>
              </div>
            </div>

          </div>        

          <!-- Row 2: waveform only -->
          <div class="mob-row mob-row-waveform d-f ai-c g-sm m-t m-b">
            <AudioWaveformPlayer
              ref="playerMob"
              :key="'mob-' + demo.id"
              class="mob-waveform flex-1 min-w-0"
              :src="peaks.length > 0 ? undefined : demo.url"
              :download-url="demo.originalUrl || demo.url"
              :peaks="peaks"
              :duration="demo.duration"
              :title="demo.title"
              :compact="true"
              :height="36"
              @seekedTo="playerSeeked"
              @downloaded="audioDownloaded"
            />
          </div>

          <!-- Row 3: "> X interactions" / "( Not reviewed )" + genre + PLAY -->
          <div class="mob-row mob-row-4 d-f ai-c jc-sb g-sm bs-bb of-h">
            <div
              class="d-f ai-c flex-1 min-w-0 custom-btn no-btn-shadow mob-interactions-wrap"
              @click.prevent="toggleReviewers"
            >
              <span v-if="demoReviewersList.length > 0" class="rotate-transition chevron-width" :class="{ 'rotate-90': reviewersOpen }">
                <IconBase :w="10" :h="10" sw="1.5" view-box="0 0 15 15"><IconChevronRight /></IconBase>
              </span>
              <span v-if="demoReviewersList.length > 0" class="mob-interactions-text">
                {{ demoReviewersList.length }} interaction{{ demoReviewersList.length === 1 ? "" : "s" }}
              </span>
              <span v-else class="mute">(Not reviewed)</span>
            </div>
            <div class="d-f ai-c g-sm flex-shrink-0">
              <span v-if="demo.genre" class="mute mob-genre">{{ demo.genre }}</span>
              <div type="button" class="mob-play-btn tt-up m-l-sm" @click="playPausePlayer">PLAY</div>
            </div>
          </div>
        </template>

        <!-- Message mode (same structure as Demo mode, waveform → latest message) -->
        <template v-else>
          <!-- Row 1: avatar + title/subtitle + 2m ago NEW + more (match Demo row 1) -->
          <div class="mob-row mob-row-1 d-f ai-c jc-sb g-sm bs-bb">
            <div
              class="mob-title-block d-f ai-c flex-1 min-w-0 custom-btn"
              @click="titleClicked"
            >
              <ImageWithInitials
                :size="40"
                :src="lastMessageData.otherProfileImage"
                :name="lastMessageData.otherProfileName"
                class="mob-avatar flex-shrink-0"
              />
              <div class="mob-title-subtitle d-f fd-c flex-1 min-w-0 of-h">
                <div class="d-f ai-c">
                  <div class="d-f ai-c flex-1 of-h bs-bb">
                    <span class="mob-title truncate">{{ demo.title }}</span>
                    <IconBase :w="10" :h="10" viewBox="0 0 15 15" sw="1.5" class="m-l-sm m-r-sm fs-0">
                      <IconChevronRight />
                    </IconBase>
                  </div>
                  <div
                    class="mob-more-wrap p-rel fs-0 d-f ai-c jc-c"
                    :class="{ 'mob-more-wrap-open': moreOpen }"
                    @click.stop="moreOpen = !moreOpen"
                  >
                    <div
                      type="button"
                      class="mob-more-btn p-l d-f"
                      aria-haspopup="true"
                      :aria-expanded="moreOpen"
                    >
                      <IconBase :w="15" :h="15" view-box="0 0 15 15" filled>
                        <IconMore />
                      </IconBase>
                    </div>
                  </div>
                </div>
                <div class="d-f ai-c">
                  <span class="mob-subtitle mute truncate">
                    {{ !isReceiver ? "Sent to: " + lastMessageData.otherProfileName : lastMessageData.otherProfileName }}
                  </span>
                  <div class="d-f ai-c m-l-sm">
                    <span class="mute"><TimestampCountdown :start="timeSince" /></span>
                    <span v-if="notViewedFlag" class="mob-new m-l-sm">NEW</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Row 2: latest message (same row pattern as Demo waveform row) -->
          <div class="mob-row mob-row-waveform d-f ai-c g-sm m-t m-b">
            <!-- <ImageWithInitials
              :size="40"
              :src="lastMessageData.senderImg"
              :name="lastMessageData.senderName"
              class="mob-avatar flex-shrink-0"
            /> -->
            <div class="mob-msg-meta d-f fd-c flex-1 min-w-0">
              <!-- <div class="mob-msg-row-a d-f ai-c mute">
                <span class="truncate">{{ lastMessageData.senderName }}</span>
                <span class="mob-sep">|</span>
                <TimestampCountdown :start="timeSince" />
              </div> -->
              <h1 
              class="mob-msg-latest m-0" 
              @click="$emit('open-chat')">{{ lastMessageData.message }}</h1>
            </div>
          </div>

          <!-- Row 3: "> X interactions" / "( Not reviewed )" + MESSAGES (count) (match Demo row 3) -->
          <div class="mob-row mob-row-4 d-f ai-c jc-sb g-sm bs-bb of-h">
            <div
              class="d-f ai-c flex-1 min-w-0 custom-btn no-btn-shadow mob-interactions-wrap"
              @click.prevent="toggleReviewers"
            >
              <span v-if="demoReviewersList.length > 0" class="rotate-transition chevron-width" :class="{ 'rotate-90': reviewersOpen }">
                <IconBase :w="10" :h="10" sw="1.5" view-box="0 0 15 15"><IconChevronRight /></IconBase>
              </span>
              <span v-if="demoReviewersList.length > 0" class="mob-interactions-text">
                {{ demoReviewersList.length }} interaction{{ demoReviewersList.length === 1 ? "" : "s" }}
              </span>
              <span v-else class="mute">(Not reviewed)</span>
            </div>
            <div class="d-f ai-c g-sm flex-shrink-0">
              <div type="button" class="mob-messages-btn tt-up m-l-sm" @click="$emit('open-chat')">
                MESSAGES ({{ messagesCount.count }})
              </div>
            </div>
          </div>
        </template>
        <DemoReviewersTable
            :is-open="reviewersOpen"
            :reviewers-list="demoReviewersList"
            :parsed-data="reviewerParsedData"
            :all-reviewers="allReviewers"
          />
      </div>
    </div>

    <!-- Desktop layout -->
    <div class="demo-tile-desktop hide-mob d-f w-100 fd-c p-hor bs-bb">
      <div class="d-f fd-c p-ver b-bottom">
        <div class="d-f flex-1 jc-c">
          <div class="demo-img p-rel m-r">
            <ImageWithInitials :size="80" :src="profileImage" :name="imageText" />
            <!-- <div
              v-if="!showMessage"
              class="overlay-btn transition-visibility"
              :class="{ 'visibility-visible': hovering || playing }"
              @click="playPausePlayer"
            >
              <div class="demo-play-btn d-f ai-c jc-c w-80 h-80 p-a pos-center border-sm b-r-100">
                <IconBase
                  :view-box="playing ? '0 0 15 15' : '0 0 15 15'" :h="30" :w="30" :sw="0.5">
                  <IconPause v-if="playing" />
                  <IconPlay v-else />
                </IconBase>
              </div>
            </div> -->
          </div>
          <div class="flex-1 d-f fd-c">
            <div class="flex-1 d-f fd-c">
              <div class="info-row d-f ai-c">
                <div class="d-f ai-c flex-1">
                  <div class="sender-info d-f ai-c m-r-sm">
                    <div v-if="showMessage" class="mute">
                      <component
                        :is="
                          lastMessageData.senderProfileUrlId
                            ? 'router-link'
                            : 'span'
                        "
                        :to="{
                          path: `/${lastMessageData.senderProfileUrlId}`,
                        }"
                        class="d-f g-half ai-c"
                      >
                        <ImageWithInitials
                          :src="lastMessageData.senderImg"
                          :name="lastMessageData.senderName"
                          size="20"
                        />
                        {{ lastMessageData.senderName }}
                      </component>
                    </div>
                    <div v-else-if="isReceiver" class="mute">
                      <router-link :to="{ path: `/${submittedByUrlId}` }">{{
                        submittedByName
                      }}</router-link>
                    </div>
                    <div v-else>
                      <span class="mute">Sent to: </span>
                      <router-link :to="{ path: `/${submittedToUrlId}` }">{{
                        submittedToName
                      }}</router-link>
                    </div>
                    <!-- <span class="m-l-sm m-r-sm grey o-5">|</span> -->
                  </div>
                  <TimestampCountdown class="mute" :start="timeSince" />
                  <div class="new-badge d-f ai-c m-l-sm" v-show="notViewedFlag">
                    {{ notViewedFlag }}
                  </div>
                  <!-- <span v-if="demo.timestamp" class="m-l-sm font-small" :class="daysUntilOriginalExpiry <= 7 ? 'expiry-warning' : 'mute'">{{ originalExpiryShort }}</span> -->

                  <div class="d-f ai-c flex-1 jc-fe">
                    <div v-if="!showMessage" class="mute">
                      {{ demo.genre }}
                    </div>
                    <div v-if="!showMessage && demo.duration" class="mute m-l-sm">
                      {{ formatDuration(demo.duration) }}
                    </div>
                    <div v-if="!showMessage" class="desktop-more-wrap p-rel m-l-sm" ref="desktopMoreWrap">
                      <button
                        type="button"
                        class="desktop-more-btn mini-icon pill transparent d-f ai-c jc-c p-half"
                        aria-haspopup="true"
                        :aria-expanded="desktopMoreOpen"
                        @click.stop="desktopMoreOpen = !desktopMoreOpen"
                      >
                        <IconBase :w="15" :h="15" view-box="0 0 15 15" filled>
                          <IconMore />
                        </IconBase>
                      </button>
                      <div v-if="desktopMoreOpen" class="desktop-more-dropdown bs-bb bc-black p">
                        <button class="secondary tt-up w-100" type="button" role="menuitem" @click="doMoreAction('download')">Download Raw File</button>
                        <span v-if="demo.timestamp" class="d-f jc-c font-small m-t" :class="daysUntilOriginalExpiry <= 7 ? 'expiry-warning' : 'mute'">{{ originalExpiryShort }}</span>
                        <!-- <button class="secondary tt-up w-100 m-b-half" type="button" role="menuitem" v-if="userType === 'label'" @click="doMoreAction('rate')">Rate</button>
                        <button class="secondary tt-up w-100 m-b-half" type="button" role="menuitem" v-if="userType === 'label' && selectedReviewer" @click="doMoreAction(savedByMe ? 'unsave' : 'save')">
                          {{ savedByMe ? 'Unsave' : 'Save' }}
                        </button>
                        <button class="secondary tt-up w-100 m-b-half" type="button" role="menuitem" v-if="userType === 'label' && selectedReviewer" @click="doMoreAction(rejectedByMe ? 'unreject' : 'reject')">
                          {{ rejectedByMe ? 'Unreject' : 'Reject' }}
                        </button>
                        <button class="secondary tt-up w-100" type="button" role="menuitem" @click="doMoreAction('message')">Message</button> -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="title-row d-f g ai-c">
                <h1
                  class="main-title m-0 no-btn-shadow m-r-auto custom-btn"
                  @click="titleClicked"
                >
                  {{
                    showMessage
                      ? lastMessageData.message
                      : demo.title
                  }}
                </h1>
                <AudioWaveformPlayer
                  v-if="!showMessage"
                  ref="playerDesktop"
                  :key="demo.id"
                  class="w-100 tile-waveform hide-mob"
                  :src="peaks.length > 0 ? undefined : demo.url"
                  :download-url="demo.originalUrl || demo.url"
                  :peaks="peaks"
                  :duration="demo.duration"
                  :title="demo.title"
                  :compact="true"
                  :height="40"
                  @seekedTo="playerSeeked"
                  @downloaded="audioDownloaded"
                />
                <!-- <h1 class="m-0 text-outline ta-r" style="width: 2.5em">
                  {{ averageRating }}
                </h1> -->
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1">
          <div class="DemoReviewersListWrapper d-f m-t g ai-c">
            <div
              class="d-f ai-c flex-1 custom-btn no-btn-shadow"
              @click.prevent="toggleReviewers"
            >
              <span
                v-if="demoReviewersList.length > 0"
                class="rotate-transition chevron-width"
                :class="{ 'rotate-90': reviewersOpen }"
              >
                <IconBase :w="15" :h="15" view-box="0 0 15 15">
                  <IconChevronRight />
                </IconBase>
              </span>
              <div class="flex-1 custom-btn no-btn-shadow">
                <div v-show="!reviewersOpen" class="demo-reviews-list">
                  <DemoReviewsList :demo-data="demo" />
                </div>
              </div>
            </div>

            <div class="actions-row d-f ai-c jc-fe">
              <div v-if="!showMessage" class="d-f g tt-up hide-mob">
                <p
                  v-if="isReceiver && activeReviewerId && savedByMe"
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="handleSave(false)"
                >
                  Unsave
                </p>
                <p
                  v-if="isReceiver && activeReviewerId && !savedByMe"
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="handleSave(true)"
                >
                  Save
                </p>
                <p
                  v-if="isReceiver && activeReviewerId && rejectedByMe"
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="handleReject(false)"
                >
                  Unreject
                </p>
                <p
                  v-if="isReceiver && activeReviewerId && !rejectedByMe"
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="handleReject(true)"
                >
                  Reject
                </p>
                <p
                  v-if="isReceiver"
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="ratingsModalOpen = true"
                >
                  Rate
                </p>
                <!-- <p
                  v-show="hovering || playing"
                  class="link hover-mute"
                  @click="downloadAudio"
                >
                  Download
                </p> -->
                <div
                  v-if="!showMessage && hovering || playing"
                  type="button"
                  class="link hover-mute"
                  :class="messagesCount.count > 0 ? 'active-color' : 'mute'"
                  @click="$emit('open-chat')"
                  >
                    {{ messagesCount.text }}
                </div>
                <!-- <p v-if="isLoading" v-show="hovering" class="mute">Loading...</p>
                  <p v-else-if="playing" class="link" @click="pausePlayer">Pause</p>
                  <p v-else class="link" @click="playPlayer">Play</p> -->
              </div>
              <div
                v-if="showMessage"
                type="button"
                class="link hover-mute tt-up m-l-sm"
                :class="messagesCount.count > 0 ? 'active-color' : 'mute'"
                @click="$emit('open-chat')"
              >
                {{ messagesCount.text }}
              </div>
              <div v-else type="button" class="mob-play-btn tt-up m-l-sm link" @click="playPausePlayer">PLAY</div>
            </div>
          </div>
        </div>
        <DemoReviewersTable
          :is-open="reviewersOpen"
          :reviewers-list="demoReviewersList"
          :parsed-data="reviewerParsedData"
          :all-reviewers="allReviewers"
        />
      </div>
    </div>


    <!-- More actions modal (mobile): on top of all content -->
    <Modal
      v-if="moreOpen"
      class="mob-more-modal"
      @hide="moreOpen = false"
      :width="200"
    >
      <div class="mob-more-modal-actions d-f fd-c g-half p-0 p">
        <button class="secondary tt-up" type="button" role="menuitem" v-if="isReceiver && activeReviewerId" @click="doMoreAction(savedByMe ? 'unsave' : 'save')">
          {{ savedByMe ? 'Unsave' : 'Save' }}
        </button>
        <button class="secondary tt-up" type="button" role="menuitem" v-if="isReceiver && activeReviewerId" @click="doMoreAction(rejectedByMe ? 'unreject' : 'reject')">
          {{ rejectedByMe ? 'Unreject' : 'Reject' }}
        </button>
        <button class="secondary tt-up" type="button" role="menuitem" v-if="isReceiver" @click="doMoreAction('rate')">Rate</button>
        <button class="secondary tt-up" type="button" role="menuitem" @click="doMoreAction('message')">Message</button>
        <button class="secondary tt-up d-f fd-c" type="button" role="menuitem" @click="doMoreAction('download')">Download Raw File <span v-if="demo.timestamp" class="d-f jc-c font-small" :class="daysUntilOriginalExpiry <= 7 ? 'expiry-warning' : 'mute'">{{ originalExpiryShort }}</span></button>
        
      </div>
    </Modal>

    <Modal
      v-if="ratingsModalOpen"
      @hide="ratingsModalOpen = false"
      inner-class="ratings-modal"
      container-padding="12px"
    >
      <div class="d-f fd-c p">
        <div class="w-100 ta-c p-rel">
          <span>Rate demo</span>
          <div
            class="pointer p-a r-0 t-0"
            @click="ratingsModalOpen = false"
          >
            <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="close">
              <IconCancel />
            </IconBase>
          </div>
        </div>
        <DemoRatings ref="demoRatingsRef" :demo-data="demo" />
        <div class="d-f m-t g">
          <button
            class="secondary flex-1"
            @click.prevent="ratingsModalOpen = false"
          >
            Cancel
          </button>
          <button class="flex-1" @click.prevent="saveRatings">Save</button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import PlaceholderImg from "@/assets/icons/PlaceholderImg.vue";
import IconBase from "@/assets/icons/IconBase.vue";
import IconChevronDown from "@/assets/icons/IconChevronDown.vue";
import IconChevronRight from "@/assets/icons/IconChevronRight.vue";
import { mapGetters, mapState } from "vuex";
import {
  dateToFormattedDT,
  formatDuration,
  getBoolText,
  getSortedObjArrayByChild,
} from "@/common";
import * as Utils from "@/utils/demoUtils";
import { isExampleDemo } from "@/utils/demoUtils";
import AudioWaveformPlayer from "@/components/misc/AudioWaveformPlayer.vue";
import IconPause from "@/assets/icons/IconPause.vue";
import IconPlay from "@/assets/icons/IconPlay.vue";
import ImageWithInitials from "@/components/misc/ImageWithInitials.vue";
import { rtdb } from "@/db";
import Modal from "@/components/misc/modal.vue";
import DemoRatings from "@/components/demos/DemoRatings.vue";
import IconCancel from "@/assets/icons/IconCancel.vue";
import IconMusic from "@/assets/icons/IconMusic.vue";
import PlayerMixin from "@/mixins/PlayerMixin";
import { delay } from "lodash";
import DemoReviewersTable from "@/components/demos/DemoReviewersTable.vue";
import DemoReviewsList from "@/components/demos/DemoReviewsList.vue";
import TimestampCountdown from "@/components/misc/TimestampCountdown.vue";
import IconMore from "@/assets/icons/IconMore.vue";

export default {
  name: "DemoTile",
  components: {
    IconMore,
    TimestampCountdown,
    IconMusic,
    IconCancel,
    DemoRatings,
    Modal,
    ImageWithInitials,
    IconPlay,
    IconPause,
    AudioWaveformPlayer,
    IconChevronDown,
    IconChevronRight,
    IconBase,
    PlaceholderImg,
    DemoReviewersTable,
    DemoReviewsList,
  },
  props: {
    demo: Object,
    showMessage: Boolean,
    allSongsPlaylist: Array,
    playerTrackId: Number,
  },
  mixins: [PlayerMixin],
  data() {
    return {
      hovering: false,
      ratingsModalOpen: false,
      reviewersOpen: false,
      moreOpen: false,
      desktopMoreOpen: false,
    };
  },
  mounted() {
    this.$root.$on("player-seeked-to", this.rootPlayerSeeked);
    this.$root.$on("demo-player-action", this.demoPlayerAction);
    document.addEventListener("click", this.closeMoreDropdown);
  },
  beforeDestroy() {
    this.$root.$off("player-seeked-to", this.rootPlayerSeeked);
    this.$root.$off("demo-player-action", this.demoPlayerAction);
    document.removeEventListener("click", this.closeMoreDropdown);
  },
  methods: {
    isExampleDemo,
    formatDuration,
    getPlayer() {
      return this.$refs.playerMob || this.$refs.playerDesktop;
    },
    closeMoreDropdown(e) {
      if (this.$refs.desktopMoreWrap && !this.$refs.desktopMoreWrap.contains(e?.target)) {
        this.desktopMoreOpen = false;
      }
      this.moreOpen = false;
    },
    doMoreAction(action) {
      this.moreOpen = false;
      this.desktopMoreOpen = false;
      switch (action) {
        case "rate":
          this.ratingsModalOpen = true;
          break;
        case "download":
          this.handleDownload();
          break;
        case "save":
          this.handleSave(true);
          break;
        case "unsave":
          this.handleSave(false);
          break;
        case "reject":
          this.handleReject(true);
          break;
        case "unreject":
          this.handleReject(false);
          break;
        case "message":
          this.$emit("open-chat");
          break;
      }
    },
    rootPlayerSeeked(time) {
      const player = this.getPlayer();
      if (this.playing && player) {
        player.seekTo(time);
      }
    },
    handleMouseEnter() {
      this.hovering = true;
    },
    handleMouseLeave() {
      this.hovering = false;
    },
    toggleReviewers() {
      if (!this.demoReviewersList.length) return;
      this.reviewersOpen = !this.reviewersOpen;
    },
    handleDownload() {
      this.getPlayer()?.downloadAudio();

      if (
        this.demoDbDownloadedRef &&
        this.activeReviewerId &&
        !Utils.isDemoDownloadedByReviewer(this.demo, this.activeReviewerId)
      ) {
        this.demoDbDownloadedRef?.set(true);
      }
    },

    titleClicked() {
      this.$emit("click");
    },

    handleSave(save) {
      const promises = [this.demoDbSavedRef.set(!!save)];

      if (save && this.rejectedByMe) {
        promises.push(this.demoDbRejectedRef.set(false));
      }

      Promise.all(promises).then(() => {
        const message = save
          ? "Added to Saved demos list"
          : "Removed from Saved demos list";
        this.$store.dispatch("togglePopMess", message);
      });
    },

    handleReject(reject) {
      const promises = [this.demoDbRejectedRef.set(!!reject)];

      if (reject && this.savedByMe) {
        promises.push(this.demoDbSavedRef.set(false));
      }

      Promise.all(promises).then(() => {
        const message = reject
          ? "Added to Rejected demos list"
          : "Removed from Rejected demos list";
        this.$store.dispatch("togglePopMess", message);
      });
    },

    playPausePlayer() {
      if (this.playing) {
        this.pausePlayer();
      } else {
        this.playPlayer();
      }
    },

    playPlayer() {
      if (!this.playing) {
        if (this.anySongPlaying) {
          this.pauseSong(); // Pause currently playing song
        }

        this.$emit("played");
        // this.$refs.player.playPause();

        delay(() => {
          if (this.isActiveInPlayer) {
            this.playCurrentSong();
          } else {
            this.replacePlaylist({ songs: this.allSongsPlaylist }).then(() => {
              this.playSelectedSong({
                playerPosition: 0,
                trackId: this.playerTrackId,
              });
            });
          }
        }, 200);

        if (
          this.demoDbPlayedRef &&
          this.activeReviewerId &&
          !Utils.isDemoPlayedByReviewer(this.demo, this.activeReviewerId)
        ) {
          return this.demoDbPlayedRef.set(true);
        }
      }
    },

    pausePlayer() {
      if (this.playing) {
        this.$emit("paused");
        // this.$refs.player.playPause();
        // this.$refs.player.resetSeek();
        this.pauseSong();
      }
    },

    saveRatings() {
      this.$refs.demoRatingsRef.save().then(() => {
        this.ratingsModalOpen = false;
        this.$store.dispatch("togglePopMess", "Added to Rated demos list");
      });
    },
    downloadAudio() {
      this.getPlayer()?.downloadAudio();
      this.audioDownloaded();
    },
    audioDownloaded() {
      if (
        this.demoDbDownloadedRef &&
        this.activeReviewerId &&
        !Utils.isDemoDownloadedByReviewer(this.demo, this.activeReviewerId)
      ) {
        return this.demoDbDownloadedRef?.set(true);
      }
    },

    playerSeeked(time) {
      if (this.playing) {
        this.seekPlayer(time);
      }
    },

    demoPlayerAction({ action, payload }) {
      if (!this.isActiveInPlayer) {
        return;
      }

      switch (action) {
        case "rate":
          this.ratingsModalOpen = true;
          break;
        case "message":
          this.$emit("open-chat");
          break;
        case "download":
          this.handleDownload();
          break;
        case "save":
          this.handleSave(payload);
          break;
        case "reject":
          this.handleReject(payload);
          break;
      }
    },
  },
  computed: {
    ...mapState(["userType", "selectedReviewer", "seenMessagesTimestamps"]),
    ...mapGetters([
      "getProfileImageByUid",
      "getProfileNameByUid",
      "getProfileUrlIdByUid",
      "getLabelReviewersByUid",
      "isReceiver",
      "activeReviewerId",
      "myId",
      "myUid",
      "getSeenTimestampForChat",
    ]),

    imageText() {
      return this.showMessage
        ? this.lastMessageData.otherProfileName
        : this.submittedByName;
    },

    peaks() {
      return JSON.parse(this.demo.peaks ?? "[]");
    },

    isActiveInPlayer() {
      return (
        this.activePlaylist?.songs &&
        this.activePlaylist.songs[this.activePlayer.currentTrackId]?.songUid ===
          this.demo.id
      );
    },

    anySongPlaying() {
      return this.activePlayer.isPlaying;
    },

    playing() {
      return this.isActiveInPlayer && this.activePlayer.isPlaying;
    },

    isLoading() {
      return this.isActiveInPlayer && this.activePlayer.playerIsLoading;
    },

    profileImage() {
      if (this.showMessage) {
        return this.lastMessageData.otherProfileImage;
      }

      if (!this.isReceiver) {
        return this.getProfileImageByUid(this.demo.submittedTo);
      }

      return this.getProfileImageByUid(this.demo.submittedBy);
    },

    // Get profile name of submittedBy user. Can be artist or label
    submittedByName() {
      return this.getProfileNameByUid(this.demo.submittedBy);
    },

    submittedToName() {
      return this.getProfileNameByUid(this.demo.submittedTo);
    },

    submittedByUrlId() {
      return this.getProfileUrlIdByUid(this.demo.submittedBy);
    },

    submittedToUrlId() {
      return this.getProfileUrlIdByUid(this.demo.submittedTo);
    },

    timeSince() {
      return new Date(
        this.showMessage ? this.lastMessageData.timestamp : this.demo.timestamp
      );
    },

    notViewedFlag() {
      if (this.showMessage) {
        return this.lastMessageData.isNew ? "NEW" : "";
      }

      if (this.isReceiver) {
        const reviewerId = this.activeReviewerId;
        if (!reviewerId) return "";
        return !Utils.isDemoPlayedByReviewer(this.demo, reviewerId) ? "NEW" : "";
      }

      return Utils.isDemoPlayed(this.demo) ? "" : "NEW";
    },

    averageRating() {
      if (this.showMessage) {
        return this.lastMessageData.isNew ? "NEW" : "";
      }

      const rating = Utils.getDemoAverageRating(this.demo);

      if (this.isReceiver) {
        const reviewerId = this.activeReviewerId;
        if (!reviewerId) return "";
        if (!Utils.isDemoPlayedByReviewer(this.demo, reviewerId)) return "NEW";
        return isNaN(rating) ? "0.0" : rating.toFixed(1);
      }

      if (!isNaN(rating)) return rating.toFixed(1);
      return Utils.isDemoPlayed(this.demo) ? "Played" : "NEW";
    },

    messagesCount() {
      const count = Object.keys(this.demo.messages ?? {}).length;

      return {
        count,
        text: `${count} message${count === 1 ? "" : "s"}`,
      };
    },

    allReviewers() {
      return this.getLabelReviewersByUid(this.demo.submittedTo);
    },

    demoReviewersList() {
      return Object.keys(this.allReviewers).filter(
        (reviewer) =>
          Utils.isDemoPlayedByReviewer(this.demo, reviewer) ||
          Utils.isDemoReviewedByReviewer(this.demo, reviewer) ||
          Utils.isDemoRatedByReviewer(this.demo, reviewer)
      );
    },

    reviewerParsedData() {
      return Object.fromEntries(
        this.demoReviewersList.map((reviewerId) => {
          const played = Utils.isDemoPlayedByReviewer(this.demo, reviewerId);
          const ratings =
            Utils.isDemoRatedByReviewer(this.demo, reviewerId) &&
            Object.entries(this.demo.ratings[reviewerId])
              .map((r) => r.join("&nbsp;"))
              .join(", ");
          const messaged = Utils.isDemoCommentedByReviewer(
            this.demo,
            reviewerId
          );
          const saved = Utils.isDemoSavedByReviewer(this.demo, reviewerId);
          const rejected = Utils.isDemoRejectedByReviewer(
            this.demo,
            reviewerId
          );
          const downloaded = Utils.isDemoDownloadedByReviewer(
            this.demo,
            reviewerId
          );
          const viewed = Utils.isDemoViewedByReviewer(this.demo, reviewerId);

          let messagesCount = messaged
            ? Object.values(this.demo.messages).filter(
                (m) => m.from === reviewerId
              ).length
            : 0;

          const dateOrBoolText = (v) =>
            typeof v === "string" ? dateToFormattedDT(v) : getBoolText(v);

          return [
            reviewerId,
            {
              name: [
                this.allReviewers[reviewerId].name,
                this.allReviewers[reviewerId].title,
              ]
                .filter(Boolean)
                .join(", "),
              ratings: ratings || getBoolText(ratings),
              played: dateOrBoolText(played),
              messaged: dateOrBoolText(messaged),
              messagesCount: messagesCount + ` times`,
              saved: dateOrBoolText(saved),
              rejected: dateOrBoolText(rejected),
              downloaded: dateOrBoolText(downloaded),
              viewed: dateOrBoolText(viewed),
            },
          ];
        })
      );
    },

    savedByMe() {
      if (!this.activeReviewerId) return false;
      return Utils.isDemoSavedByReviewer(this.demo, this.activeReviewerId);
    },

    rejectedByMe() {
      if (!this.activeReviewerId) return false;
      return Utils.isDemoRejectedByReviewer(this.demo, this.activeReviewerId);
    },

    lastMessageData() {
      if (!this.showMessage) return {};

      const lastMessage = getSortedObjArrayByChild(
        this.demo.messages,
        "desc"
      )[0];

      let senderImg = "";
      let senderName = "";
      let senderProfileUrlId = "";
      if (lastMessage.from in this.allReviewers) {
        const r = this.allReviewers[lastMessage.from];

        senderImg = r.profileImage;
        senderName = [r.name, r.title].filter(Boolean).join(" - ");
      } else if (lastMessage.from === this.demo.submittedBy) {
        senderName = this.getProfileNameByUid(this.demo.submittedBy);
        senderImg = this.getProfileImageByUid(this.demo.submittedBy);
        senderProfileUrlId = this.getProfileUrlIdByUid(this.demo.submittedBy);
      }

      const otherProfileImage = this.getProfileImageByUid(
        this.demo.submittedBy === this.myUid
          ? this.demo.submittedTo
          : this.demo.submittedBy
      );
      const otherProfileName = this.getProfileNameByUid(
        this.demo.submittedBy === this.myUid
          ? this.demo.submittedTo
          : this.demo.submittedBy
      );

      return {
        otherProfileImage,
        otherProfileName,
        senderImg,
        senderName: senderName || "-",
        senderProfileUrlId,
        message: lastMessage.message,
        timestamp: lastMessage.timestamp,
        isNew:
          lastMessage.from !== this.activeReviewerId &&
          this.getSeenTimestampForChat(this.demo.id) <
            new Date(lastMessage.timestamp).getTime(),
      };
    },

    demoDbRef() {
      return rtdb.ref(`demoSubmissions/${this.demo.id}`);
    },

    demoDbPlayedRef() {
      if (!this.activeReviewerId) return null;
      return this.demoDbRef.child(`played/${this.activeReviewerId}`);
    },

    demoDbDownloadedRef() {
      if (!this.activeReviewerId) return null;
      return this.demoDbRef.child(`downloaded/${this.activeReviewerId}`);
    },

    demoDbSavedRef() {
      if (!this.activeReviewerId) return null;
      return this.demoDbRef.child(`saved/${this.activeReviewerId}`);
    },

    demoDbRejectedRef() {
      if (!this.activeReviewerId) return null;
      return this.demoDbRef.child(`rejected/${this.activeReviewerId}`);
    },

    daysUntilOriginalExpiry() {
      if (!this.demo?.timestamp) return 30;
      const submitted = new Date(this.demo.timestamp).getTime();
      if (isNaN(submitted)) return 30;
      const elapsed = Date.now() - submitted;
      return Math.max(0, 30 - Math.floor(elapsed / (24 * 60 * 60 * 1000)));
    },

    originalExpiryShort() {
      if (!this.demo?.originalUrl && this.daysUntilOriginalExpiry <= 0) return "Expired";
      const d = this.daysUntilOriginalExpiry;
      if (d === 0) return "Expires today";
      return `${d} d left`;
    },
  },
  watch: {
    userType() {},
    selectedReviewer() {},
    "activePlayer.currentTrackId": {
      handler(newVal) {
        const player = this.getPlayer();
        if (!this.isActiveInPlayer && player) {
          player.resetSeek();
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/scss/_variables.scss";
@import "@/scss/_mixins.scss";
@import "@/scss/_classes.scss";

/* Ratings modal: full width on mobile, fixed width on desktop */
::v-deep .ratings-modal {
  @media #{$mobile} {
    width: 100%;
  }
  @media (min-width: 668px) {
    width: 500px;
  }
}

.expiry-warning {
  color: $red;
}

.desktop-more-wrap {
  position: relative;
  flex-shrink: 0;
}

.desktop-more-btn {
  cursor: pointer;
  border: none;
  border-radius: 4px;
  min-width: 32px;
  min-height: 32px;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.desktop-more-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 200px;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  button {
    display: block;
    width: 100%;
    text-align: left;
    // padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    background: transparent;
    white-space: nowrap;
    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: $white;
    }
  }
}

/* Allow accordion table to expand without being clipped */
.demo-tile {
  overflow: visible;
  &:hover {
    background-color: $card-bg-color;
    .demo-img {
      background-color: $card-bg-color;
    }
  }

}

/* Mobile layout: show only on mobile */
.demo-tile-mob {
  display: none;
  flex-direction: column;
  width: 100%;
  gap: 6px;

  @media #{$mobile} {
    display: flex;
  }
}

.mob-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.mob-row-1 {
  gap: 8px;
}

.mob-title-block {
  min-width: 0;
  cursor: pointer;
  gap: 8px;
}

.mob-title-subtitle {
  gap: 2px;
}

.mob-subtitle {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mob-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mob-chevron {
  flex-shrink: 0;
  opacity: 0.7;
}

.mob-more-wrap {
  flex-shrink: 0;
  position: relative;
  z-index: 2; /* above title link so more button receives taps in Demo mode */
  /* 44px touch target via padding; negative margin keeps row height unchanged */
  padding: 14px;
  margin: -14px -14px -14px 0;
}

.mob-more-wrap-open {
  z-index: 100; /* dropdown must sit above following rows (artist, waveform, etc.) */
}

/* More actions modal: 8px border radius on the modal box */
.mob-more-modal::v-deep .modal-inner {
  border-radius: 8px;
}

/* More actions modal: list of buttons on top of all content */
.mob-more-modal-actions {
  min-width: 160px;
}

.mob-sep {
  opacity: 0.5;
  margin: 0 4px;
}

.mob-row-waveform {
  gap: 0;
}

.mob-interactions-wrap {
  gap: 4px;
}

.mob-row-3 {
  gap: 10px;
}

.mob-avatar {
  flex-shrink: 0;
}

.mob-waveform {
  min-width: 0;
  flex: 1;
}

.mob-latest-message {
  font-size: 1rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.mob-row-4 {
  gap: 8px;
}

.mob-reviewers-list-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/* Message mode mobile */
.mob-msg-r1-left {
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.mob-msg-to-avatar {
  flex-shrink: 0;
}

.mob-msg-row2 {
  gap: 12px;
  align-items: flex-start;
}

.mob-msg-sender-avatar {
  flex-shrink: 0;
}

.mob-msg-meta {
  min-width: 0;
}

.mob-msg-row-a {
  font-size: 0.9rem;
  flex-wrap: wrap;
}

.mob-msg-latest {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.tile-waveform {
  max-width: 50%;
  max-height: 50px;

  @media #{$desktop} {
    max-width: 50%;
  }
  @media #{$large-screen} {
    max-width: 30%;
  }
}

.rotate-transition {
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rotate-transition.rotate-90 {
  transform: rotate(90deg);
}

.chevron-width {
  width: 20px;
  flex-shrink: 0;
  @media #{$mobile} {
    width: 15px;
  }
}
.active-color {
  color: $white !important;
}
.main-title {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2;
  padding-bottom: 2px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: normal;
}
</style>
